import type { ColumnsType } from 'antd/lib/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { useGetTopSalesPersonsQuery } from '../../../../api/queries/getTopSalesPersons';
import { type StatisticFilterPayload, ModuleRole, ApplicationStage } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import useFormats from '../../../../utilities/useFormats';
import { type TopSalesManData } from './useTopSalesManWithApplicationData';

export interface TopSalesManWithLoanData {
    key: string;
    name: string;
    alias: string;
    formattedLoanAmount: string;
}

const columns = (t: TFunction): ColumnsType<TopSalesManWithLoanData> => [
    {
        ...t('dashboard:topSalesManWithLoan.tableColumns.name', { returnObjects: true }),
        dataIndex: 'name',
        key: 'name',
        fixed: true,
    },
    {
        ...t('dashboard:topSalesManWithLoan.tableColumns.alias', { returnObjects: true }),
        dataIndex: 'alias',
        key: 'alias',
    },
    {
        ...t('dashboard:topSalesManWithLoan.tableColumns.loan', { returnObjects: true }),
        dataIndex: 'formattedLoanAmount',
        key: 'formattedLoanAmount',
    },
];

const useTopSalesManWithLoanData = () => {
    const { t } = useTranslation(['dashboard']);
    const cols = useMemo(() => columns(t), [t]);
    const [isLoading, setIsLoading] = useState(false);

    // prepare filter
    const company = useCompany(true);
    const { dealerIds } = useMultipleDealerIds();
    const filter: StatisticFilterPayload = useMemo(
        () => ({
            companyId: company?.id,
            dealerIds,
            moduleRole: ModuleRole.Application,
            stage: ApplicationStage.Financing,
        }),
        [company, dealerIds]
    );

    // fetch data
    const { data, loading, error } = useGetTopSalesPersonsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter,
            sortParam: 'amount',
        },
        skip: !company?.id,
    });

    useEffect(() => {
        setIsLoading(loading);
    }, [loading]);

    const { formatAmountWithCurrency } = useFormats(
        company?.currency || '',
        company?.roundings?.amount?.decimals || 0,
        company?.roundings?.percentage?.decimals || 0
    );

    // transform response data
    const generateData = useCallback(
        async (applyData: (data: TopSalesManData<TopSalesManWithLoanData>) => void) => {
            if (!data?.metrics) {
                applyData(null);

                return;
            }

            setIsLoading(true);

            const currentMonth: TopSalesManWithLoanData[] =
                data.metrics.currentMonth?.map(({ name, alias, amount }) => ({
                    key: name,
                    name,
                    alias,
                    formattedLoanAmount: formatAmountWithCurrency(amount || 0),
                })) || [];
            const lastMonth: TopSalesManWithLoanData[] =
                data.metrics.lastMonth?.map(({ name, alias, amount }) => ({
                    key: name,
                    name,
                    alias,
                    formattedLoanAmount: formatAmountWithCurrency(amount || 0),
                })) || [];

            applyData({ columns: cols, data: { currentMonth, lastMonth } });
            setIsLoading(false);
        },
        [data, cols, formatAmountWithCurrency]
    );

    return useMemo(
        () => ({
            isLoading,
            error,
            generateData,
        }),
        [isLoading, error, generateData]
    );
};

export default useTopSalesManWithLoanData;
